import { useEffect, useState } from 'react';
import { X, Tv, Trash2, Copy, Check, Plus, Download, Home, Globe2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { FamilyMember, XtreamCredential, XtreamCredentialMint } from '@lib/streamFlowApi';
import { PUBLIC_API_URL, TAILNET_API_URL } from '@lib/apiBase';

type PlatformKey = 'shield' | 'firetv' | 'ios' | 'android' | 'desktop';

interface PlatformInfo {
  key: PlatformKey;
  label: string;
  app: string;
  installHint: string;
  steps: string[];
}

const PLATFORMS: PlatformInfo[] = [
  {
    key: 'shield',
    label: 'Android TV / Shield',
    app: 'Tivimate',
    installHint: 'Play Store → search "Tivimate IPTV Player"',
    steps: [
      'Open Tivimate, choose "Add playlist"',
      'Pick "Xtream Codes" and paste the URL, username, and password below',
      '(or scan the QR code below with the Tivimate companion if installed)',
      'Tivimate fetches your channels — favorites, EPG, all standard',
    ],
  },
  {
    key: 'firetv',
    label: 'Fire TV / Fire Stick',
    app: 'IPTV Smarters Pro',
    installHint: 'Amazon Appstore → search "IPTV Smarters" (or sideload via Downloader)',
    steps: [
      'Open Smarters, choose "Login with Xtream Codes API"',
      'Enter any name for the playlist',
      'Paste the URL, username, and password below',
      'Smarters loads the channel list — D-pad navigates',
    ],
  },
  {
    key: 'ios',
    label: 'iPhone / iPad',
    app: 'IPTV Smarters Pro',
    installHint: 'App Store → search "IPTV Smarters Player"',
    steps: [
      'Install the Tailscale app first (for tailnet egress)',
      'Open Smarters, choose "Login with Xtream Codes API"',
      'Paste the URL, username, and password below',
      'Channel list loads — favorites + EPG work natively',
    ],
  },
  {
    key: 'android',
    label: 'Android phone / tablet',
    app: 'Tivimate or IPTV Smarters',
    installHint: 'Play Store → either works; Tivimate has better EPG',
    steps: [
      'Install the Tailscale app first (for tailnet egress)',
      'Open your IPTV app, choose Xtream Codes login',
      'Paste the URL, username, and password below',
    ],
  },
  {
    key: 'desktop',
    label: 'Desktop / Laptop',
    app: 'OTT Navigator or VLC',
    installHint: 'OTT Navigator (Windows): ottnavigator.com — VLC works with the .m3u link',
    steps: [
      'Install Tailscale on the desktop for tailnet egress',
      'OTT Navigator: Add account → Xtream → paste the URL/user/pass',
      'VLC: Media → Open Network Stream → paste the M3U link below',
    ],
  },
];

interface Props {
  member: FamilyMember;
  onClose: () => void;
}

/**
 * Per-member "IPTV devices" modal: lists existing Xtream-protocol
 * credentials this family member has minted (for Tivimate / Smarters /
 * OTT Navigator / VLC) and lets them mint a new one.
 *
 * The plaintext password is only ever shown once — at creation time —
 * so the modal opens a "credentials minted" view immediately after
 * generation. They copy/paste into the IPTV app or download the .m3u.
 */
export default function IPTVDeviceModal({ member, onClose }: Props) {
  const [creds, setCreds] = useState<XtreamCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [justMinted, setJustMinted] = useState<XtreamCredentialMint | null>(null);

  // Strip trailing /api so the URL handed to IPTV apps doesn't double up
  // (the apps add their own /xtream paths). We expose BOTH hosts so the
  // user picks based on the device's networking: tailnet host for any
  // device with Tailscale installed at the OS level (native IPTV apps
  // on Android/iOS respect this even when the browser doesn't), public
  // host as the always-reachable fallback.
  const tailnetHost = TAILNET_API_URL.replace(/\/api$/, '');
  const publicHost = PUBLIC_API_URL.replace(/\/api$/, '');

  const refresh = async () => {
    const r = await streamFlowApi.listXtreamCredentials();
    if (r.success) setCreds((r.data ?? []).filter((c) => c.member_id === member.id));
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [member.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();
    setMinting(true);
    const r = await streamFlowApi.createXtreamCredential({
      member_id: member.id,
      label: newLabel || undefined,
    });
    setMinting(false);
    if (r.success && r.data) {
      setJustMinted(r.data);
      setNewLabel('');
      refresh();
    } else {
      toast.error(r.error?.message || 'Mint failed');
    }
  };

  const handleDelete = async (id: string, label: string | null) => {
    if (!confirm(`Revoke "${label || id.slice(0, 8)}"? The IPTV app on that device will stop working.`)) return;
    const r = await streamFlowApi.deleteXtreamCredential(id);
    if (r.success) {
      toast.success('Credential revoked');
      refresh();
    } else {
      toast.error(r.error?.message || 'Revoke failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Tv className="w-5 h-5 text-blue-600" />
              IPTV devices for {member.display_name}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Each credential is a username/password pair you paste into Tivimate, IPTV
              Smarters, OTT Navigator, or VLC on a TV / phone / Fire Stick.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {justMinted ? (
          <MintedView creds={justMinted} tailnetHost={tailnetHost} publicHost={publicHost} onDone={() => setJustMinted(null)} />
        ) : (
          <>
            <div className="px-5 py-4 space-y-3">
              <form onSubmit={handleMint} className="flex gap-2">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Device label (e.g. Living Room Fire TV)"
                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                />
                <button
                  type="submit"
                  disabled={minting}
                  className="px-3 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  {minting ? 'Minting…' : 'New device'}
                </button>
              </form>

              {loading ? (
                <p className="text-sm text-gray-500 py-4 text-center">Loading…</p>
              ) : creds.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  No IPTV devices yet. Mint one above and paste the credentials into your app.
                </p>
              ) : (
                <div className="space-y-2">
                  {creds.map((c) => (
                    <div key={c.id} className="border rounded-lg p-3 flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.label || '(unlabeled)'}</p>
                        <p className="text-[11px] text-gray-500 font-mono truncate">user: {c.username}</p>
                        <p className="text-[10px] text-gray-400">
                          {c.last_used_at ? `last used ${new Date(c.last_used_at).toLocaleString()}` : 'never used'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDelete(c.id, c.label)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                        aria-label="Revoke"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-20 flex-shrink-0">{label}</span>
      <code className="flex-1 text-xs bg-gray-100 px-2 py-1 rounded font-mono truncate">{value}</code>
      <button
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="p-1.5 hover:bg-gray-100 rounded"
        aria-label="Copy"
      >
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
      </button>
    </div>
  );
}

function MintedView({ creds, tailnetHost, publicHost, onDone }: { creds: XtreamCredentialMint; tailnetHost: string; publicHost: string; onDone: () => void }) {
  // Two orthogonal choices:
  //   - endpoint host (tailnet vs public) — affects URL + QR encoding
  //   - target platform — affects which app + steps we show
  const [host, setHost] = useState<'tailnet' | 'public'>('tailnet');
  const [platform, setPlatform] = useState<PlatformKey>('shield');
  const activeHost = host === 'tailnet' ? tailnetHost : publicHost;
  const m3uUrl = `${activeHost}/api/xtream/get.php?username=${encodeURIComponent(creds.username)}&password=${encodeURIComponent(creds.password)}`;
  const activePlatform = PLATFORMS.find((p) => p.key === platform) ?? PLATFORMS[0];

  return (
    <div className="px-5 py-4 space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
        ⚠ This is the only time the password is shown. Save it somewhere or paste into your app now.
      </div>

      {/* Step 1: endpoint host */}
      <div>
        <p className="text-sm font-medium mb-2">1. Endpoint</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setHost('tailnet')}
            className={`text-left p-3 border rounded-lg ${host === 'tailnet' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Home className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">Tailnet (recommended)</span>
            </div>
            <p className="text-[11px] text-gray-600">
              Bytes flow through home. Works on any device with Tailscale installed at the OS level — including iOS / Android with the Tailscale app.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setHost('public')}
            className={`text-left p-3 border rounded-lg ${host === 'public' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Globe2 className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium">Public (Azure)</span>
            </div>
            <p className="text-[11px] text-gray-600">
              Fallback for devices without Tailscale (smart TVs, hotel TVs, friends' devices).
            </p>
          </button>
        </div>
      </div>

      {/* Step 2: platform picker */}
      <div>
        <p className="text-sm font-medium mb-2">2. Where are you setting up?</p>
        <div className="flex flex-wrap gap-1.5">
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPlatform(p.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                platform === p.key
                  ? 'border-slate-800 bg-slate-800 text-white'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Step 3: setup card per platform */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-sm font-semibold">{activePlatform.label} → {activePlatform.app}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{activePlatform.installHint}</p>
          </div>
        </div>
        <ol className="text-xs text-gray-700 space-y-1 mb-3 list-decimal list-inside">
          {activePlatform.steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <div className="space-y-1.5">
            <CopyRow label="URL"      value={activeHost} />
            <CopyRow label="Username" value={creds.username} />
            <CopyRow label="Password" value={creds.password} />
          </div>
          {/* QR encodes the M3U link — any QR-aware Xtream client (or
              the phone's camera) can scan this to one-shot the setup
              instead of typing the 3 values. */}
          <div className="flex flex-col items-center justify-center bg-white border rounded-lg p-2">
            <QRCodeSVG value={m3uUrl} size={128} level="M" includeMargin={false} />
            <p className="text-[10px] text-gray-500 mt-1.5">Scan to configure</p>
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Or download the .m3u file</p>
        <p className="text-xs text-gray-500 mb-2">Pre-fills the connection in any IPTV/HLS player that accepts an M3U URL.</p>
        <div className="flex gap-2">
          <code className="flex-1 text-xs bg-gray-100 px-2 py-1.5 rounded font-mono truncate">{m3uUrl}</code>
          <a
            href={m3uUrl}
            download={`streamflow-${creds.username}-${host}.m3u`}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-500 flex items-center gap-1"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </a>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          onClick={onDone}
          className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
        >
          Done — back to device list
        </button>
      </div>
    </div>
  );
}
